import React, { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { createAsyncThunk } from '@reduxjs/toolkit'
import styled from 'styled-components'
import Form from 'react-bootstrap/Form'
import Card from 'react-bootstrap/Card'
import Button from 'react-bootstrap/cjs/Button'

import { _ } from '../../../utils/localization'
import Icon from '../../../components/Icon'
import colors from '../../../constants/colors'
import { RootState } from '../../../store'
import sendMessage from '../../chat/sendMessage'
import { User } from '../../auth/types'
import { currentUserSelector } from '../../auth/authSlice'
import { showNotification } from '../../notifications'
import { toggleIsQuestionsOpened } from '../questionsSlice'
import { toLocalTimeFormat } from '../../../utils/datetime'
import { moderatorSelector, presentationSelector } from '../presentationSlice'
import useSlug from '../../../hooks/useSlug'

interface QuestionsWrapperProps {
    show: boolean
}

const S = {
    Form: styled(Form)`
        display: flex;
        align-items: center;
        justify-content: space-between;
    `,
    SendButton: styled(Button)`
        background-color: transparent !important;  // important to disable hover
        border-color: transparent;
    `,
    Input: styled(Form.Control)`
        border: 0;
    `,
    QuestionsWrapper: styled.div<QuestionsWrapperProps>`
        height: auto; 
        pointer-events: ${({ show }) => (show ? 'auto' : 'none')};// to remove all events to div when question is not opened.
        overflow: hidden;
        opacity: ${({ show }) => +show};
        transition: opacity .3s, height .3s;
    `,
    CardHeader: styled.div`
        display: flex;
        align-items: center;
        justify-content: space-between;
        color: ${colors.gray6};
        font-weight: bold;
    `,
    SystemMessage: styled.div`
        padding: 8px;
    `,
}

const askQuestion = (
    eventSlug: string,
    text: string,
    currentUser: User,
    moderator: number,
    pageData
) => createAsyncThunk(
    'ASK_QUESTION',
    (args, thunkAPI) => {
        const presentation = (pageData && pageData?.data?.current_presentation) || {}
        const start = toLocalTimeFormat(presentation.start_date)
        const end = toLocalTimeFormat(presentation.end_date)
        const body = `Question @ ${presentation.name} | ${start} - ${end}\n${text}`
        thunkAPI.dispatch(sendMessage({
            eventSlug,
            text: body,
            currentUserId: currentUser.id,
            receiverId: moderator,
        }))
        thunkAPI.dispatch(toggleIsQuestionsOpened())
        thunkAPI.dispatch(showNotification({
            body: _('presentation.questions.is_sent_notification.body'),
            type: 'info',
        }))
    },
)()

const QuestionInput = () => {
    const eventSlug = useSlug('eventSlug')
    const presentationSlug = useSlug('presentationSlug')
    const dispatch = useDispatch()
    const data = useSelector(presentationSelector(presentationSlug))
    const currentUser = useSelector(currentUserSelector())
    const moderator = useSelector(moderatorSelector(presentationSlug))
    const [text, setText] = useState('')
    const submit = (e) => {
        e.preventDefault()
        dispatch(askQuestion(eventSlug, text, currentUser, moderator, data))
        setText('')
    }

    if (!moderator) {
        return (
            <S.SystemMessage>
                {/*
                        This case should not happen in production,
                        So no translation key is added
                 */}
                Questions are not available for this presentation
            </S.SystemMessage>
        )
    }

    if (moderator === currentUser.id) {
        return (
            <S.SystemMessage>{_('presentation.questions.current_user_is_moderator')}</S.SystemMessage>
        )
    }

    return (
        <S.Form onSubmit={submit}>
            <S.Input
                type="text"
                value={text}
                onChange={(e) => setText(e.target.value)}
                placeholder={_('presentation.questions.question_field_placeholder')}
            />
            <S.SendButton>
                <Icon icon="send" onClick={submit} />
            </S.SendButton>
        </S.Form>
    )
}

const Questions = () => {
    const dispatch = useDispatch()
    const selectIsOpened = (state: RootState) => state.presentationRoom.questions.isOpened
    const isOpened = useSelector(selectIsOpened)
    const close = () => { dispatch(toggleIsQuestionsOpened()) }
    return (
        <S.QuestionsWrapper show={isOpened}>
            <Card style={{ width: '100%' }}>
                <Card.Header as={S.CardHeader}>
                    {_('presentation.questions.ask_your_question')}
                    <Icon icon="close" onClick={close} />
                </Card.Header>

                <QuestionInput />
            </Card>
        </S.QuestionsWrapper>
    )
}

export default Questions
